import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useI18n } from '../i18n';
import type { StringKey } from '../i18n';
import { RESULT_FILTERS, countResults, filterResults } from '../lib/resultFilter';
import type { ResultFilter } from '../lib/resultFilter';
import type { CheckResult } from '../lib/types';
import { SegmentedButton, SegmentedButtonRow } from './ui/Controls';
import { LinkTile } from './ui/LinkTile';
import { TopAppBar } from './ui/ScreenScaffold';

/**
 * The Results tab — a port of `android/.../ui/result/ResultScreen.kt`.
 *
 * Three parts, in Android's order: a title bar with **no back arrow** (Results
 * is a peer tab, not a pushed destination — and a WebClip has no Back button to
 * pair one with anyway), the three-way filter carrying live counts, and the list
 * of tiles separated by hairlines.
 *
 * ### It renders a fragment, not a wrapper
 *
 * `TopAppBar` is `flex: 0 0 auto` and the list is `flex: 1 1 auto`; both expect
 * to be direct children of the flex column `MainShell` puts each tab in
 * (`.tab-pane`). An extra `<div>` here would swallow the height the list needs
 * to become its own scroller, and the list *has* to be the scroller: the filter
 * must stay put while several hundred rows move under it. A control you have to
 * scroll back up to reach is the one people use most on this screen.
 *
 * ### What is deliberately absent
 *
 * No HTTP status column, anywhere. `statusCode` is always `null` on this
 * platform (SPEC §6) and a column of blanks invites somebody to fill it in.
 * And no open-in-browser action — see the note in `LinkTile.tsx`; the tile
 * exposes no `onOpen` prop, so this screen has no snackbar for Android's
 * `result_no_browser` and that string is not among the ported keys.
 */
export function ResultScreen({ results }: { results: CheckResult[] }): JSX.Element {
  const { t, n } = useI18n();

  /**
   * Android holds this in `rememberSaveable`, which survives both recomposition
   * and process death. The web half of that guarantee is structural rather than
   * an API: `MainShell` keeps all three tabs mounted and hides the inactive
   * ones, so plain `useState` outlives a trip to Home and back. Do not "fix"
   * this by lifting it into the shell or persisting it — a filter that resets to
   * All every time somebody glances at the dashboard is the exact papercut
   * `rememberSaveable` exists to prevent, and a filter that *persists across
   * sessions* is worse: an operator opening the app to a Dead-only list has no
   * clue why the fleet looks dead.
   */
  const [filter, setFilter] = useState<ResultFilter>('all');
  const [toast, setToast] = useState<Toast | null>(null);

  /**
   * The numbers on the bar. Counted in `src/lib/resultFilter.ts`, next to the
   * predicates `filterResults` uses, because the counts and the lists have to
   * agree — a bar reading `Dead 1` over a Dead tab showing nothing is the shape
   * of a filter that quietly drops rows. Written out here the two were one edit
   * apart from disagreeing.
   *
   * Memoised for the same reason Android wraps these in `remember(results)`:
   * every toast that appears and auto-dismisses re-renders this screen twice,
   * and re-walking a few hundred rows per copied URL is work nobody asked for.
   */
  const counts = useMemo(() => countResults(results), [results]);

  /**
   * The filtering rule itself lives in `src/lib/resultFilter.ts` and is tested
   * there. It is not inlined here on purpose: it is the one part of this screen
   * that can be wrong in a way nobody sees, because a filter that quietly drops
   * rows looks exactly like a short list.
   */
  const shown = useMemo(() => filterResults(results, filter), [results, filter]);

  const showToast = useCallback((text: string) => {
    // The counter, not just the text, is what makes a second copy of the same
    // URL re-announce and restart the timer: an `aria-live` region whose content
    // did not change is not read out again, and React would keep the existing
    // node (and its running timeout) if nothing about the state differed.
    setToast((previous) => ({ text, id: previous === null ? 1 : previous.id + 1 }));
  }, []);

  /**
   * Auto-dismiss, the stand-in for `SnackbarHostState.showSnackbar` — Compose
   * owns the timer; on the web it has to be written out. Keyed on the whole
   * toast object so a fresh message restarts the clock rather than inheriting
   * the tail of the previous one's.
   *
   * The cleanup also covers unmount, which matters more here than it looks: the
   * shell can drop this tab while a toast is up, and a `setState` fired
   * afterwards is a warning in the console of a device nobody is watching.
   */
  useEffect(() => {
    if (toast === null) return;
    const handle = window.setTimeout(() => setToast(null), TOAST_MS);
    return () => window.clearTimeout(handle);
  }, [toast]);

  /**
   * Copy the URL, and say so.
   *
   * Both branches speak. A copy button that silently does nothing is
   * indistinguishable from a copy button that worked, and the operator finds out
   * only when they paste the previous link into a ticket. `navigator.clipboard`
   * is genuinely absent in some situations this app can meet — a non-secure
   * origin during local testing, or a write refused because iOS did not consider
   * the tap a user gesture — so the property access itself is inside the `try`,
   * where a `TypeError` lands in the same place a rejected promise does.
   */
  const copy = useCallback(
    async (url: string): Promise<void> => {
      try {
        await navigator.clipboard.writeText(url);
        showToast(t('result_copied'));
      } catch {
        showToast(t('ios_copy_failed'));
      }
    },
    [showToast, t],
  );

  return (
    <>
      <TopAppBar title={t('nav_results')} />

      {/*
       * No `label` on the group: the row sits directly under the screen's <h1>,
       * which already names it. A second name here would make VoiceOver read
       * "Results" twice before the first segment.
       */}
      <SegmentedButtonRow className="result-filter">
        {RESULT_FILTERS.map((value) => (
          <SegmentedButton
            key={value}
            label={t(FILTER_LABEL_KEYS[value], n(counts[value]))}
            selected={filter === value}
            onClick={() => setFilter(value)}
          />
        ))}
      </SegmentedButtonRow>

      {shown.length === 0 ? (
        <p className="result-empty t-body-large">{t('result_empty')}</p>
      ) : (
        <div className="result-list">
          {shown.map((result) => (
            // A Fragment rather than a wrapper <div>: `.result-list` is the
            // flex column and `.link-tile` is written to be its direct child,
            // so an element in between would have to re-declare the row's
            // layout to avoid changing it.
            //
            // Keyed on the backend's link id, as Android keys its `items`. The
            // URL would look like a fine key right up to the day two brands
            // share a domain, at which point React starts reusing the wrong row.
            <Fragment key={result.link.id}>
              <LinkTile result={result} onCopy={(url) => void copy(url)} />
              {/* After every tile, the last one included — matching Android,
                * where `HorizontalDivider()` is emitted inside the item block.
                * The list's own bottom padding keeps the final rule off the
                * tab bar. */}
              <hr className="hairline" />
            </Fragment>
          ))}
        </div>
      )}

      {toast === null ? null : (
        /*
         * Compose's `SnackbarHost`, rebuilt as a plain fixed element (`.toast`
         * clears the tab bar and the home indicator). `role="status"` +
         * `aria-live="polite"` because it is confirmation, not an alert: it must
         * never interrupt VoiceOver mid-sentence as somebody reads a row.
         */
        <div key={toast.id} className="toast t-body-medium" role="status" aria-live="polite">
          {toast.text}
        </div>
      )}
    </>
  );
}

/** A message plus the nonce that lets an identical message re-announce. */
interface Toast {
  text: string;
  id: number;
}

/**
 * 4s — Material 3's `SnackbarDuration.Short`, so the same confirmation does not
 * linger for a different length of time on the two apps.
 */
const TOAST_MS = 4000;

/**
 * Filter value → its label key. The values were chosen to be the key suffixes
 * (`filter_all` / `filter_alive` / `filter_dead`), but the mapping is written
 * out rather than built by concatenation: `t()` takes a `StringKey`, and a
 * key assembled from a template is a `string`, which throws away the compile
 * error that is the whole point of the type.
 *
 * Each label carries its count as `{0}` — Android's `stringResource(labelRes,
 * formatIntForLocale(count))`, so the number is grouped in the reader's own
 * locale.
 */
const FILTER_LABEL_KEYS = {
  all: 'filter_all',
  alive: 'filter_alive',
  dead: 'filter_dead',
} as const satisfies Record<ResultFilter, StringKey>;
